import React from "react";

interface AeroMonthTabsProps {
    selectedMonth: number;
    selectedYear: number;
    onMonthChange: (month: number, year: number) => void;
}

const SHORT_MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

/* Aero's actual arrow SVGs — 20x20, fill white, inside 24x24 circles */
const ArrowLeft: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" fill="currentColor">
        <path d="M30 46.5 9.28 24 30 1.5l9 0L18.56 24 39 46.5l-9 0Z" />
    </svg>
);
const ArrowRight: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" fill="currentColor">
        <path d="M18 1.5 38.72 24 18 46.5l-9 0L29.44 24 9 1.5l9 0Z" />
    </svg>
);

const AeroMonthTabs: React.FC<AeroMonthTabsProps> = ({
    selectedMonth,
    selectedYear,
    onMonthChange,
}) => {
    const now = new Date();
    const startMonth = now.getMonth();
    const startYear = now.getFullYear();

    const months: { month: number; year: number; label: string }[] = [];
    for (let i = 0; i < 6; i++) {
        const m = (startMonth + i) % 12;
        const y = startYear + Math.floor((startMonth + i) / 12);
        months.push({ month: m, year: y, label: SHORT_MONTHS[m] });
    }

    const currentIndex = months.findIndex(
        (m) => m.month === selectedMonth && m.year === selectedYear,
    );

    const handlePrev = () => {
        if (currentIndex <= 0) return;
        const prev = months[currentIndex - 1];
        onMonthChange(prev.month, prev.year);
    };

    const handleNext = () => {
        if (currentIndex >= months.length - 1) return;
        const next = months[currentIndex + 1];
        onMonthChange(next.month, next.year);
    };

    const prevDisabled = currentIndex <= 0;
    const nextDisabled = currentIndex >= months.length - 1;

    return (
        <div className="flex items-center" style={{ marginTop: 8 }}>
            {/* Month tabs */}
            <div className="flex items-center" style={{ gap: 24, flex: 1 }}>
                {months.map((m) => {
                    const isActive =
                        m.month === selectedMonth && m.year === selectedYear;
                    return (
                        <button
                            key={`${m.year}-${m.month}`}
                            onClick={() => onMonthChange(m.month, m.year)}
                            style={{
                                fontSize: 16,
                                fontWeight: 500,
                                color: isActive ? "#000" : "#949494",
                                background: "none",
                                border: "none",
                                borderBottom: isActive
                                    ? "2px solid #fb6900"
                                    : "2px solid transparent",
                                padding: "8px 0",
                                cursor: "pointer",
                                whiteSpace: "nowrap" as const,
                            }}
                        >
                            {m.label}
                        </button>
                    );
                })}
            </div>

            {/* Nav arrows — 24x24 circles, prev #c6c6c6, next #000 */}
            <div
                className="flex items-center"
                style={{ gap: 8, marginLeft: "auto", flexShrink: 0 }}
            >
                <button
                    onClick={handlePrev}
                    disabled={prevDisabled}
                    className="flex items-center justify-center"
                    style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: "#c6c6c6",
                        border: "none",
                        padding: 0,
                        cursor: prevDisabled ? "default" : "pointer",
                        color: "#fff",
                        opacity: prevDisabled ? 0.5 : 1,
                    }}
                >
                    <ArrowLeft />
                </button>
                <button
                    onClick={handleNext}
                    disabled={nextDisabled}
                    className="flex items-center justify-center"
                    style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: nextDisabled ? "#c6c6c6" : "#000",
                        border: "none",
                        padding: 0,
                        cursor: nextDisabled ? "default" : "pointer",
                        color: "#fff",
                    }}
                >
                    <ArrowRight />
                </button>
            </div>
        </div>
    );
};

export default AeroMonthTabs;
